import { useLocation } from 'react-router-dom'

import {
  ET_EDITOR_QUERY_PARAM,
  ET_EDITOR_VARIANTS,
  type EtEditorVariant,
} from 'shared/constants/et_editor'

const VALID_VARIANTS = new Set<string>(Object.values(ET_EDITOR_VARIANTS))

function isEtEditorVariant(value: string | null): value is EtEditorVariant {
  return value !== null && VALID_VARIANTS.has(value)
}

/** Reads `et_editor` from a `location.search` string (with or without leading `?`). */
export function readEtEditorQueryParam(search = ''): EtEditorVariant | null {
  const value = new URLSearchParams(search).get(ET_EDITOR_QUERY_PARAM)
  return isEtEditorVariant(value) ? value : null
}

/** Current URL's `et_editor` override, or null when absent/invalid. */
export function useEtEditorQueryParam(): EtEditorVariant | null {
  const { search } = useLocation()
  return readEtEditorQueryParam(search)
}

/**
 * Appends or replaces `et_editor` on a path. Pass null/undefined to leave search unchanged
 * when `variant` is null; strips only when explicitly clearing is needed elsewhere.
 */
export function withEtEditorQueryParam(
  path: string,
  variant?: EtEditorVariant | null,
): string {
  if (!variant) return path

  const [pathname = '', existingSearch = ''] = path.split('?')
  const params = new URLSearchParams(existingSearch)
  params.set(ET_EDITOR_QUERY_PARAM, variant)
  const nextSearch = params.toString()
  return nextSearch ? `${pathname}?${nextSearch}` : pathname
}
